import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Plus,
  FolderKanban,
  MessageSquare,
  Sparkles,
  Loader2,
  MoreHorizontal,
  Trash2,
  Files,
  Archive,
  ArchiveRestore,
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import apiService from '../../services/api';
import { useChatContext } from '../../context/ChatContext';
import { AgentAvatar } from '../AgentAvatar';
import { DOMAINS, DOMAIN_LABELS, DEFAULT_DOMAIN, personaForDomain, type AgentDomain } from '../../utils/agentCatalog';
import type { ProjectSummary } from './AppShell';
import type { ChatSession } from '../../types';

interface Props {
  projects: ProjectSummary[];
  /** Mobile drawer closes the sidebar after a navigation tap. */
  onNavigate?: () => void;
}

/** Chats shown per agent before "הצג עוד" — keeps all three agent sections,
    the projects list and the archive visible without scrolling in the common
    case. Expansion reveals the rest (the sidebar body scrolls). */
const INITIAL_CHATS_SHOWN = 5;
const ARCHIVE_CHATS_LIMIT = 30;
const PROJECTS_LIMIT = 8;

const sessionLabel = (s: ChatSession): string => {
  const title = (s.title || '').trim();
  return title || 'שיחה חדשה';
};

type SessionBucket = { key: string; label: string; sessions: ChatSession[] };

/** Group a recency-sorted session list into date buckets (today / yesterday /
    last 7 days / older) so a long list stays scannable. Sessions older than
    the server's auto-archive window (7 days) normally never reach this list —
    the backend sweeps them into the archive on every sidebar load. */
const bucketByRecency = (sessions: ChatSession[]): SessionBucket[] => {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 24 * 3600 * 1000;
  const startOfWeek = startOfToday - 6 * 24 * 3600 * 1000;
  const groups: Record<'today' | 'yesterday' | 'week' | 'older', ChatSession[]> = {
    today: [], yesterday: [], week: [], older: [],
  };
  for (const s of sessions) {
    const t = new Date(s.updated_at || s.created_at).getTime();
    if (t >= startOfToday) groups.today.push(s);
    else if (t >= startOfYesterday) groups.yesterday.push(s);
    else if (t >= startOfWeek) groups.week.push(s);
    else groups.older.push(s);
  }
  return [
    { key: 'today', label: 'היום', sessions: groups.today },
    { key: 'yesterday', label: 'אתמול', sessions: groups.yesterday },
    { key: 'week', label: '7 הימים האחרונים', sessions: groups.week },
    { key: 'older', label: 'ישן יותר', sessions: groups.older },
  ].filter((b) => b.sessions.length > 0);
};

/** Read a boolean from localStorage with a default fallback. */
const readPersistedBool = (key: string, fallback: boolean): boolean => {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === '1') return true;
    if (raw === '0') return false;
  } catch {
    /* localStorage may be blocked (Safari private mode) — fall through */
  }
  return fallback;
};

const writePersistedBool = (key: string, value: boolean) => {
  try {
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* ignore */
  }
};

const Sidebar: React.FC<Props> = ({ projects, onNavigate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const routeParams = useParams<{ projectId?: string; chatId?: string }>();
  const { clearChat, setActiveDomain } = useChatContext();

  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [archivedSessions, setArchivedSessions] = useState<ChatSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  // Domains where the user clicked "הצג עוד" to reveal chats beyond the
  // initial 5. Session-scoped on purpose — a fresh load starts compact.
  const [expandedDomains, setExpandedDomains] = useState<Set<AgentDomain>>(new Set());
  const toggleDomainExpanded = (domain: AgentDomain) => {
    setExpandedDomains((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });
  };

  // Section collapse state — persisted to localStorage so the user's
  // arrangement survives reloads. The three domain sections + Projects are
  // open by default; Archive collapsed by default.
  const [domainOpen, setDomainOpen] = useState<Record<AgentDomain, boolean>>(() => {
    const init = {} as Record<AgentDomain, boolean>;
    for (const d of DOMAINS) init[d] = readPersistedBool(`sidebar.domainOpen.${d}`, true);
    return init;
  });
  const [projectsOpen, setProjectsOpen] = useState(() =>
    readPersistedBool('sidebar.projectsOpen', true),
  );
  const [archiveOpen, setArchiveOpen] = useState(() =>
    readPersistedBool('sidebar.archiveOpen', false),
  );

  const toggleDomain = (domain: AgentDomain) => {
    setDomainOpen((prev) => {
      const next = !prev[domain];
      writePersistedBool(`sidebar.domainOpen.${domain}`, next);
      return { ...prev, [domain]: next };
    });
  };
  const toggleProjects = () => {
    setProjectsOpen((v) => {
      const next = !v;
      writePersistedBool('sidebar.projectsOpen', next);
      return next;
    });
  };
  const toggleArchive = () => {
    setArchiveOpen((v) => {
      const next = !v;
      writePersistedBool('sidebar.archiveOpen', next);
      // Lazy-load the archive list the first time the user opens the drawer
      // so we don't pay the network cost for users who never look at it.
      if (next && archivedSessions.length === 0) {
        refreshArchived();
      }
      return next;
    });
  };

  const refreshSessions = async () => {
    setSessionsLoading(true);
    try {
      const data = await apiService.getChatSessions();
      data.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
      setSessions(data);
    } catch {
      // Quiet failure — the recent-chats list is a nice-to-have, not critical.
    } finally {
      setSessionsLoading(false);
    }
  };

  const refreshArchived = async () => {
    try {
      const data = await apiService.getChatSessions({ archived: true });
      data.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
      setArchivedSessions(data);
    } catch {
      /* same: quiet failure */
    }
  };

  useEffect(() => {
    refreshSessions();
    // If the archive section is open from a previous session, load it too.
    if (archiveOpen) refreshArchived();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh recents whenever the user lands on a chat route. Cheap and keeps
  // the sidebar in sync after a new chat is created or renamed.
  useEffect(() => {
    if (location.pathname.includes('/c/')) refreshSessions();
  }, [location.pathname]);

  const handleNewChat = () => {
    setActiveDomain(DEFAULT_DOMAIN);
    clearChat();
    onNavigate?.();
    const target = routeParams.projectId ? `/p/${routeParams.projectId}/c/new` : '/c/new';
    navigate(target);
  };

  const handleNewChatInDomain = (domain: AgentDomain) => {
    setActiveDomain(domain);
    clearChat();
    onNavigate?.();
    const target = routeParams.projectId ? `/p/${routeParams.projectId}/c/new` : '/c/new';
    navigate(target);
  };

  const handleDeleteSession = async (sessionId: string) => {
    setOpenMenuId(null);
    try {
      await apiService.deleteChatSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      setArchivedSessions((prev) => prev.filter((s) => s.id !== sessionId));
      if (routeParams.chatId === sessionId) {
        clearChat();
        navigate('/c/new', { replace: true });
      }
    } catch {
      toast.error('מחיקת השיחה נכשלה');
    }
  };

  const handleArchiveSession = async (sessionId: string) => {
    setOpenMenuId(null);
    const moved = sessions.find((s) => s.id === sessionId);
    try {
      await apiService.archiveChatSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      if (moved) {
        setArchivedSessions((prev) => [moved, ...prev.filter((s) => s.id !== sessionId)]);
      }
      // Don't auto-redirect — the user might still want to read the archived
      // conversation. Just toast the action.
      toast.success('השיחה הועברה לארכיון');
    } catch {
      toast.error('העברה לארכיון נכשלה');
    }
  };

  const handleUnarchiveSession = async (sessionId: string) => {
    setOpenMenuId(null);
    const moved = archivedSessions.find((s) => s.id === sessionId);
    try {
      await apiService.unarchiveChatSession(sessionId);
      setArchivedSessions((prev) => prev.filter((s) => s.id !== sessionId));
      if (moved) {
        setSessions((prev) => [moved, ...prev.filter((s) => s.id !== sessionId)]);
      }
      toast.success('השיחה שוחזרה');
    } catch {
      toast.error('שחזור השיחה נכשל');
    }
  };

  // Close the per-row "⋯" menu when clicking anywhere else.
  useEffect(() => {
    if (!openMenuId) return;
    const close = () => setOpenMenuId(null);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openMenuId]);

  const activeChatPath = (sessionId: string) =>
    routeParams.projectId
      ? `/p/${routeParams.projectId}/c/${sessionId}`
      : `/c/${sessionId}`;

  /** One conversation row — NavLink + hover "⋯" actions (archive / delete). */
  const renderSessionRow = (s: ChatSession) => (
    <div key={s.id} className="relative group">
      <NavLink
        to={activeChatPath(s.id)}
        onClick={() => {
          setActiveDomain((s.domain ?? 'financial') as AgentDomain);
          onNavigate?.();
        }}
        end
        className={({ isActive }) =>
          `flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs transition-colors ${
            isActive
              ? 'bg-sidebar-800 text-white'
              : 'text-sidebar-300 hover:bg-sidebar-800/60 hover:text-white'
          }`
        }
      >
        <MessageSquare className="h-3.5 w-3.5 flex-shrink-0 text-sidebar-500" />
        <span className="truncate flex-1">{sessionLabel(s)}</span>
      </NavLink>
      <button
        onMouseDown={(e) => {
          e.stopPropagation();
          setOpenMenuId(openMenuId === s.id ? null : s.id);
        }}
        className="absolute inset-y-0 left-1 my-auto h-6 w-6 rounded text-sidebar-500 opacity-0 group-hover:opacity-100 hover:bg-sidebar-700 hover:text-white flex items-center justify-center transition-opacity"
        title="פעולות"
        aria-label="פעולות לשיחה"
      >
        <MoreHorizontal className="h-3.5 w-3.5" />
      </button>
      {openMenuId === s.id && (
        <div className="absolute left-0 top-7 z-50 w-40 bg-sidebar-800 border border-sidebar-700 rounded-lg shadow-xl py-1 text-xs origin-top-left animate-chat-bubble motion-reduce:animate-none">
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => handleArchiveSession(s.id)}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-sidebar-200 hover:bg-sidebar-700 transition-colors"
          >
            <Archive className="h-3.5 w-3.5" />
            העבר לארכיון
          </button>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => handleDeleteSession(s.id)}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-error-400 hover:bg-sidebar-700 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
            מחק שיחה
          </button>
        </div>
      )}
    </div>
  );

  /** Reusable section header — chevron rotates, label is clickable. */
  const SectionHeader: React.FC<{
    label: React.ReactNode;
    open: boolean;
    onToggle: () => void;
    right?: React.ReactNode;
  }> = ({ label, open, onToggle, right }) => (
    <div className="flex items-center justify-between px-1 mb-1.5">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-sidebar-500 hover:text-sidebar-300 transition-colors"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronLeft className="h-3 w-3" />
        )}
        {label}
      </button>
      {right}
    </div>
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col text-sidebar-200" dir="rtl">
      {/* New chat — pinned above the scrollable body */}
      <div className="p-3 flex-shrink-0 space-y-2">
        <button
          onClick={handleNewChat}
          className="group w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium press"
          title="שיחה חדשה (⌘K)"
        >
          <Plus className="h-4 w-4" />
          שיחה חדשה
          <kbd className="ms-auto hidden md:inline-flex items-center gap-0.5 text-[10px] font-mono px-1 py-0.5 rounded border border-white/20 bg-white/10 text-white/70 opacity-0 group-hover:opacity-100 transition-opacity">
            ⌘K
          </kbd>
        </button>
        <NavLink
          to="/reports"
          onClick={onNavigate}
          className="flex min-h-10 w-full items-center gap-2 rounded-lg border border-sidebar-700/80 px-3 text-sm font-medium text-sidebar-200 transition-colors hover:border-sidebar-600 hover:bg-sidebar-800 hover:text-white"
        >
          <Building2 className="h-4 w-4 text-primary-400" />
          דוחות ציבוריים
          <span className="mr-auto text-[10px] text-sidebar-500">מרחב נפרד</span>
        </NavLink>
      </div>

      {/* Scrollable body — conversations, projects and archive. The sidebar
          (not the page) owns this overflow, so a long chat list can never push
          the projects/archive sections or the footer out of reach ("the list
          slides off the edge" bug). */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">

      {/* Conversations — one collapsible section per domain. Each conversation
          is bound to a domain (appraisal / legal / financial); a session with
          no domain falls back to financial. */}
      {DOMAINS.map((domain) => {
        const domainSessions = sessions.filter(
          (s) => (s.domain ?? 'financial') === domain,
        );
        const persona = personaForDomain(domain);
        return (
          <div key={domain} className="px-3 pb-3">
            {/* Agent header — the persona that answers in this domain. Larger
                avatar + name + role so each agent reads clearly (the sidebar has
                room); the whole row toggles the section, with a "+ new chat"
                action at the end. */}
            <div className="flex items-center justify-between gap-2 px-1 mb-2">
              <button
                type="button"
                onClick={() => toggleDomain(domain)}
                className="group flex items-center gap-2.5 flex-1 min-w-0"
                aria-expanded={domainOpen[domain]}
                title={`${persona.name} — ${persona.tagline}`}
              >
                <AgentAvatar domain={domain} size={38} className="flex-shrink-0" />
                <span className="flex flex-col items-start min-w-0 leading-tight">
                  <span className="text-sm font-semibold text-sidebar-100 truncate max-w-full">
                    {persona.name}
                  </span>
                  <span className="text-[11px] text-sidebar-400 truncate max-w-full">
                    {persona.tagline}
                  </span>
                </span>
                {domainOpen[domain] ? (
                  <ChevronDown className="h-4 w-4 flex-shrink-0 text-sidebar-500 group-hover:text-sidebar-300 transition-colors" />
                ) : (
                  <ChevronLeft className="h-4 w-4 flex-shrink-0 text-sidebar-500 group-hover:text-sidebar-300 transition-colors" />
                )}
              </button>
              <div className="flex items-center gap-1 flex-shrink-0">
                {sessionsLoading ? (
                  <Loader2 className="h-3.5 w-3.5 text-sidebar-500 animate-spin" />
                ) : null}
                <button
                  type="button"
                  onClick={() => handleNewChatInDomain(domain)}
                  className="flex items-center gap-1 text-[11px] text-sidebar-400 hover:text-white hover:bg-sidebar-800 px-1.5 py-1 rounded-md transition-colors whitespace-nowrap"
                  title={`שיחה חדשה — ${DOMAIN_LABELS[domain]}`}
                  aria-label={`שיחה חדשה — ${DOMAIN_LABELS[domain]}`}
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">חדשה</span>
                </button>
              </div>
            </div>
            {domainOpen[domain] && (
              domainSessions.length === 0 && !sessionsLoading ? (
                <div className="text-[11px] text-sidebar-500 px-1 leading-relaxed">
                  אין עדיין שיחות.
                </div>
              ) : (
                <div className="space-y-0.5">
                  {(() => {
                    const isExpanded = expandedDomains.has(domain);
                    const shown = isExpanded
                      ? domainSessions
                      : domainSessions.slice(0, INITIAL_CHATS_SHOWN);
                    const buckets = bucketByRecency(shown);
                    // Date sub-headers only earn their space once the list
                    // spans more than one bucket.
                    const showBucketLabels = buckets.length > 1;
                    const hiddenCount = domainSessions.length - shown.length;
                    return (
                      <>
                        {buckets.map((bucket) => (
                          <React.Fragment key={bucket.key}>
                            {showBucketLabels && (
                              <div className="px-2.5 pt-1.5 pb-0.5 text-[10px] text-sidebar-600 select-none">
                                {bucket.label}
                              </div>
                            )}
                            {bucket.sessions.map(renderSessionRow)}
                          </React.Fragment>
                        ))}
                        {hiddenCount > 0 && (
                          <button
                            type="button"
                            onClick={() => toggleDomainExpanded(domain)}
                            className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[11px] text-sidebar-400 hover:bg-sidebar-800/60 hover:text-white transition-colors"
                          >
                            <ChevronDown className="h-3 w-3" />
                            הצג עוד {hiddenCount} שיחות
                          </button>
                        )}
                        {isExpanded && domainSessions.length > INITIAL_CHATS_SHOWN && (
                          <button
                            type="button"
                            onClick={() => toggleDomainExpanded(domain)}
                            className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[11px] text-sidebar-400 hover:bg-sidebar-800/60 hover:text-white transition-colors"
                          >
                            <ChevronUp className="h-3 w-3" />
                            הצג פחות
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              )
            )}
          </div>
        );
      })}

      {/* Projects */}
      <div className="px-3 pb-3 border-t border-sidebar-800/60 pt-3">
        <SectionHeader
          label="פרויקטים"
          open={projectsOpen}
          onToggle={toggleProjects}
          right={
            <NavLink
              to="/p"
              onClick={onNavigate}
              className="text-[10px] text-sidebar-500 hover:text-sidebar-300 transition-colors"
            >
              כל הפרויקטים →
            </NavLink>
          }
        />
        {projectsOpen && (
          projects.length === 0 ? (
            <div className="text-[11px] text-sidebar-500 px-1 leading-relaxed">
              אין עדיין פרויקטים.
            </div>
          ) : (
            <div className="space-y-0.5">
              {projects.slice(0, PROJECTS_LIMIT).map((p) => (
                <div key={p.id} className="flex items-center gap-0.5">
                  <NavLink
                    to={`/p/${p.id}/c/new`}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      `flex-1 flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs transition-colors ${
                        isActive
                          ? 'bg-sidebar-800 text-white'
                          : 'text-sidebar-300 hover:bg-sidebar-800/60 hover:text-white'
                      }`
                    }
                  >
                    <FolderKanban className="h-3.5 w-3.5 text-sidebar-500 flex-shrink-0" />
                    <span className="truncate flex-1">{p.name}</span>
                    <span className="text-[10px] text-sidebar-500">{p.document_count}</span>
                  </NavLink>
                  {/* Quick-jump to project Files page (PR #78) — clicking the
                      project name itself still opens chat; this side link goes
                      to the file browser. */}
                  <NavLink
                    to={`/p/${p.id}/files`}
                    onClick={onNavigate}
                    title="קבצי הפרויקט"
                    className={({ isActive }) =>
                      `p-1 rounded text-sidebar-500 hover:bg-sidebar-800/60 hover:text-sidebar-300 transition-colors flex-shrink-0 ${
                        isActive ? 'text-white bg-sidebar-800' : ''
                      }`
                    }
                  >
                    <Files className="h-3.5 w-3.5" />
                  </NavLink>
                </div>
              ))}
              {projects.length > PROJECTS_LIMIT && (
                <NavLink
                  to="/p"
                  onClick={onNavigate}
                  className="block px-2.5 py-1 text-[10px] text-sidebar-500 hover:text-sidebar-300"
                >
                  + {projects.length - PROJECTS_LIMIT} פרויקטים נוספים
                </NavLink>
              )}
            </div>
          )
        )}
      </div>

      {/* Archive — only shown when expanded, list is lazy-loaded on open */}
      <div className="px-3 pb-3 border-t border-sidebar-800/60 pt-3">
        <SectionHeader
          label="ארכיון"
          open={archiveOpen}
          onToggle={toggleArchive}
          right={
            archivedSessions.length > 0 ? (
              <span className="text-[10px] text-sidebar-500">{archivedSessions.length}</span>
            ) : null
          }
        />
        {archiveOpen && (
          <div className="px-1 pb-1.5 text-[10px] text-sidebar-600 leading-relaxed">
            שיחות ללא פעילות מעל 7 ימים עוברות לכאן אוטומטית.
          </div>
        )}
        {archiveOpen && (
          archivedSessions.length === 0 ? (
            <div className="text-[11px] text-sidebar-500 px-1 leading-relaxed">
              אין שיחות בארכיון.
            </div>
          ) : (
            <div className="space-y-0.5">
              {archivedSessions.slice(0, ARCHIVE_CHATS_LIMIT).map((s) => (
                <div key={s.id} className="relative group">
                  <NavLink
                    to={activeChatPath(s.id)}
                    onClick={onNavigate}
                    end
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs transition-colors ${
                        isActive
                          ? 'bg-sidebar-800 text-white'
                          : 'text-sidebar-400 hover:bg-sidebar-800/60 hover:text-white'
                      }`
                    }
                  >
                    <Archive className="h-3.5 w-3.5 flex-shrink-0 text-sidebar-600" />
                    <span className="truncate flex-1 italic">{sessionLabel(s)}</span>
                  </NavLink>
                  <button
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      setOpenMenuId(openMenuId === s.id ? null : s.id);
                    }}
                    className="absolute inset-y-0 left-1 my-auto h-6 w-6 rounded text-sidebar-500 opacity-0 group-hover:opacity-100 hover:bg-sidebar-700 hover:text-white flex items-center justify-center transition-opacity"
                    title="פעולות"
                    aria-label="פעולות לשיחה מארכיון"
                  >
                    <MoreHorizontal className="h-3.5 w-3.5" />
                  </button>
                  {openMenuId === s.id && (
                    <div className="absolute left-0 top-7 z-50 w-40 bg-sidebar-800 border border-sidebar-700 rounded-lg shadow-xl py-1 text-xs origin-top-left animate-chat-bubble motion-reduce:animate-none">
                      <button
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => handleUnarchiveSession(s.id)}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-sidebar-200 hover:bg-sidebar-700 transition-colors"
                      >
                        <ArchiveRestore className="h-3.5 w-3.5" />
                        שחזר שיחה
                      </button>
                      <button
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => handleDeleteSession(s.id)}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-error-400 hover:bg-sidebar-700 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        מחק לצמיתות
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {archivedSessions.length > ARCHIVE_CHATS_LIMIT && (
                <div className="px-2.5 py-1 text-[10px] text-sidebar-500">
                  + {archivedSessions.length - ARCHIVE_CHATS_LIMIT} שיחות נוספות בארכיון
                </div>
              )}
            </div>
          )
        )}
      </div>

      {/* End of scrollable body */}
      </div>

      {/* Footer hints + keyboard cheatsheet — pinned below the scroll area */}
      <div className="p-3 border-t border-sidebar-800/60 space-y-2 flex-shrink-0">
        <div className="flex items-start gap-2 text-[11px] text-sidebar-500 leading-relaxed">
          <Sparkles className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <span>גרור קבצים לחלון השיחה כדי לצרף אותם.</span>
        </div>
        <div className="flex items-start gap-2 text-[11px] text-sidebar-500 leading-relaxed">
          <MessageSquare className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <span>"הצג שמאות" / "בנה חיתום" פותחים artifact בצד.</span>
        </div>
        <div className="hidden md:flex flex-wrap gap-x-3 gap-y-1 pt-1 text-[10px] text-sidebar-600">
          <span><kbd className="font-mono px-1 rounded bg-sidebar-800 text-sidebar-400">⌘K</kbd> שיחה</span>
          <span><kbd className="font-mono px-1 rounded bg-sidebar-800 text-sidebar-400">⌘B</kbd> סייד-בר</span>
          <span><kbd className="font-mono px-1 rounded bg-sidebar-800 text-sidebar-400">⌘.</kbd> פאנל</span>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
